import * as THREE from 'three';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import type { EnvironmentConfig } from '../sceneConfig';
import { logBigBuffer } from '../../debug/memoryTracker';
import { loadWithCdnFallback } from '../../utils/loadWithCdnFallback';
import { fetchWithTtfbTimeout } from '../../utils/fetchWithTtfbTimeout';

const SKYBOX_FADE_DELAY = 1.5; // seconds before fade starts
const SKYBOX_FADE_DURATION = 1.5; // seconds

interface EnvironmentManagerOptions {
  scene: THREE.Scene;
  isDevMode: boolean;
  config: EnvironmentConfig;
  initialSkyboxUrl?: string;
  onVisualChange?: () => void;
}

export class EnvironmentManager {
  private readonly scene: THREE.Scene;
  private readonly isDevMode: boolean;
  private readonly config: EnvironmentConfig;
  private readonly onVisualChange?: () => void;
  private readonly textureLoader: THREE.TextureLoader;
  private loadedTexture: THREE.Texture | null = null;
  private preloadedTexture: THREE.Texture | null = null;
  private preloadedUrl: string | null = null;
  private preloadAbortId: number = 0;
  private disposed: boolean = false;
  private skyboxUrl: string | null;
  private skyboxIntensityOverride: number | null = null;
  private backgroundColor: number;
  private skyboxEnabled: boolean = false;

  // Intensity fade animation state
  private fadeFrom = 0;
  private fadeTo = 0;
  private fadeDelay = 0;
  private fadeDuration = 0;
  private fadeElapsed = 0;
  private fading = false;

  constructor(options: EnvironmentManagerOptions) {
    this.scene = options.scene;
    this.isDevMode = options.isDevMode;
    this.config = options.config;
    this.onVisualChange = options.onVisualChange;
    this.textureLoader = new THREE.TextureLoader();
    this.skyboxUrl = options.initialSkyboxUrl ?? options.config.background.skybox?.url ?? null;
    this.backgroundColor = options.config.background.color;

    if (this.skyboxUrl) {
      this.preloadSkyboxTexture(this.skyboxUrl);
    }
  }

  addLighting(): void {
    this.addGlobalLight(this.scene);
    this.setupBackground();
  }

  private addGlobalLight(targetScene: THREE.Scene): void {
    const { hemisphereLight, directionalLights } = this.config;
    const ambient = new THREE.HemisphereLight(
      hemisphereLight.skyColor,
      hemisphereLight.groundColor,
      hemisphereLight.intensity
    );
    const lightObjects = directionalLights.map((light) => {
      const directional = new THREE.DirectionalLight(light.color, light.intensity);
      directional.position.set(light.position.x, light.position.y, light.position.z);
      return directional;
    });
    targetScene.add(ambient, ...lightObjects);
  }

  /** Set up initial background - use background.color during preview phase */
  private setupBackground(): void {
    const { background } = this.config;
    this.scene.background = new THREE.Color(background.color);
    this.onVisualChange?.();
  }

  /** Switch background to the main state (post-preview) - skybox or color with fade-in */
  setMainBackground(instant = false): void {
    const { background } = this.config;
    const url = this.skyboxUrl || background.skybox?.url;
    if (url) {
      this.skyboxEnabled = true;
      const targetIntensity = this.skyboxIntensityOverride ?? background.skybox?.intensity ?? 1.0;
      if (instant) {
        this.loadSkyboxTexture(
          url,
          targetIntensity,
          background.skybox?.blur ?? 0.0,
          background.skybox?.rotation ?? [0, 0, 0],
        );
      } else {
        this.loadSkyboxTexture(
          url,
          0,
          background.skybox?.blur ?? 0.0,
          background.skybox?.rotation ?? [0, 0, 0],
        );
        this.startIntensityFade(targetIntensity, SKYBOX_FADE_DURATION, SKYBOX_FADE_DELAY);
      }
    } else {
      this.skyboxEnabled = false;
      this.scene.background = new THREE.Color(background.color);
      this.onVisualChange?.();
    }
  }

  /** Set background to a solid color */
  setBackgroundColor(color: number): void {
    this.backgroundColor = color;
    // Only apply if skybox is not currently active
    if (!(this.scene.background instanceof THREE.Texture)) {
      this.scene.background = new THREE.Color(color);
      this.onVisualChange?.();
    }
  }

  /** Enable or disable skybox */
  setSkyboxEnabled(enabled: boolean): void {
    // Only act if state actually changed
    if (enabled === this.skyboxEnabled) {
      return;
    }
    this.skyboxEnabled = enabled;

    if (enabled && this.skyboxUrl) {
      // Load skybox texture using the dynamic URL
      const skybox = this.config.background.skybox;
      this.loadSkyboxTexture(
        this.skyboxUrl,
        skybox?.intensity ?? 1.0,
        skybox?.blur ?? 0.0,
        skybox?.rotation ?? [0, 0, 0]
      );
    } else {
      // Dispose texture and show background color
      if (this.loadedTexture) {
        this.loadedTexture.dispose();
        this.loadedTexture = null;
      }
      this.clearPreload();
      this.scene.background = new THREE.Color(this.backgroundColor);
      this.onVisualChange?.();
    }
  }

  /** Set skybox URL and reload texture if skybox is active, or preload if not yet active */
  setSkyboxUrl(url: string): void {
    const normalizedUrl = url || null;
    if (normalizedUrl === this.skyboxUrl) {
      return;
    }
    this.skyboxUrl = normalizedUrl;

    if (this.skyboxEnabled && this.skyboxUrl) {
      // Reload texture while preserving current scene settings
      this.loadSkyboxTexture(
        this.skyboxUrl,
        this.scene.backgroundIntensity,
        this.scene.backgroundBlurriness,
        [
          THREE.MathUtils.radToDeg(this.scene.backgroundRotation.x),
          THREE.MathUtils.radToDeg(this.scene.backgroundRotation.y),
          THREE.MathUtils.radToDeg(this.scene.backgroundRotation.z),
        ]
      );
    } else if (this.skyboxUrl) {
      this.preloadSkyboxTexture(this.skyboxUrl);
    } else {
      this.clearPreload();
    }
  }

  /** Set skybox intensity (cancels any ongoing fade) */
  setSkyboxIntensity(intensity: number): void {
    this.skyboxIntensityOverride = intensity;
    this.fading = false;
    this.scene.backgroundIntensity = intensity;
    this.onVisualChange?.();
  }

  /** Set skybox blur (0-1) */
  setSkyboxBlur(blur: number): void {
    this.scene.backgroundBlurriness = blur;
    this.onVisualChange?.();
  }

  /** Set skybox rotation (degrees) */
  setSkyboxRotation(rotation: [number, number, number]): void {
    this.scene.backgroundRotation.set(
      THREE.MathUtils.degToRad(rotation[0]),
      THREE.MathUtils.degToRad(rotation[1]),
      THREE.MathUtils.degToRad(rotation[2])
    );
    this.onVisualChange?.();
  }

  /** Start animating backgroundIntensity from current value to target over duration after delay. */
  private startIntensityFade(target: number, duration: number, delay: number = 0): void {
    this.fadeFrom = this.scene.backgroundIntensity;
    this.fadeTo = target;
    this.fadeDelay = delay;
    this.fadeDuration = duration;
    this.fadeElapsed = 0;
    this.fading = true;
  }

  /** Advance intensity fade animation. Returns true while animating (including delay). */
  update(deltaT: number): boolean {
    if (!this.fading) {
      return false;
    }

    this.fadeElapsed += deltaT;

    if (this.fadeElapsed < this.fadeDelay) {
      return true; // waiting for delay
    }

    const elapsed = this.fadeElapsed - this.fadeDelay;
    const t = Math.min(elapsed / this.fadeDuration, 1);
    const smooth = t * t * (3 - 2 * t); // smoothstep
    this.scene.backgroundIntensity = this.fadeFrom + (this.fadeTo - this.fadeFrom) * smooth;
    this.onVisualChange?.();

    if (t >= 1) {
      this.fading = false;
    }

    return this.fading;
  }

  /**
   * Fetch a skybox URL with TTFB timeout, then hand the bytes to the appropriate
   * Three.js loader via a blob URL — so the loader's TexData→DataTexture / Image
   * decoding path stays intact while the network stage is bounded by our timeout.
   */
  private async fetchSkyboxTexture(resolvedUrl: string, isExr: boolean): Promise<THREE.Texture> {
    const response = await fetchWithTtfbTimeout(resolvedUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${resolvedUrl}: ${response.status} ${response.statusText}`);
    }
    const buffer = await response.arrayBuffer();
    const blob = new Blob([buffer]);
    const blobUrl = URL.createObjectURL(blob);
    try {
      return await new Promise<THREE.Texture>((resolve, reject) => {
        const loader = isExr ? new EXRLoader() : this.textureLoader;
        loader.load(blobUrl, resolve, undefined, reject);
      });
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  }

  /** Start loading skybox texture in the background without applying it to the scene. */
  private preloadSkyboxTexture(url: string): void {
    const loadId = ++this.preloadAbortId;
    const isExr = url.toLowerCase().endsWith('.exr');

    loadWithCdnFallback(url, (resolvedUrl) => this.fetchSkyboxTexture(resolvedUrl, isExr), { assetType: 'skybox' })
      .then((texture) => {
        if (this.disposed || loadId !== this.preloadAbortId) {
          texture.dispose();
          return;
        }
        texture.colorSpace = isExr ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace;
        texture.mapping = THREE.EquirectangularReflectionMapping;
        this.preloadedTexture = texture;
        this.preloadedUrl = url;
        // Diagnostic: report CPU-side bytes (Float32Array for HDR EXR — typical
        // 30–128 MB at 4K) so iOS heap accounting becomes visible in mem-debug
        // logs even without `performance.memory` access.
        const data = (texture.source?.data as { byteLength?: number } | null | undefined);
        logBigBuffer('skybox preloaded', data?.byteLength ?? null, {
          url,
          isExr,
          kind: 'preload',
        });
      })
      .catch((error: unknown) => {
        if (loadId !== this.preloadAbortId) return;
        console.error('Failed to preload skybox texture:', error);
      });
  }

  /** Apply a loaded texture to the scene background with settings. */
  private applyLoadedTexture(
    texture: THREE.Texture,
    intensity: number,
    blur: number,
    rotation: [number, number, number]
  ): void {
    if (this.loadedTexture) {
      this.loadedTexture.dispose();
    }
    this.scene.background = texture;
    this.loadedTexture = texture;
    this.scene.backgroundIntensity = intensity;
    this.scene.backgroundBlurriness = blur;
    this.scene.backgroundRotation.set(
      THREE.MathUtils.degToRad(rotation[0]),
      THREE.MathUtils.degToRad(rotation[1]),
      THREE.MathUtils.degToRad(rotation[2])
    );
    this.onVisualChange?.();
  }

  /** Discard any in-flight preload and cached preloaded texture. */
  private clearPreload(): void {
    this.preloadAbortId++;
    if (this.preloadedTexture) {
      this.preloadedTexture.dispose();
      this.preloadedTexture = null;
      this.preloadedUrl = null;
    }
  }

  /** Load skybox texture with settings. Uses preloaded texture if available. */
  private loadSkyboxTexture(
    url: string,
    intensity: number,
    blur: number,
    rotation: [number, number, number]
  ): void {
    // Invalidate any in-flight preload
    this.preloadAbortId++;

    // Use preloaded texture if URL matches
    if (this.preloadedTexture && this.preloadedUrl === url) {
      const texture = this.preloadedTexture;
      this.preloadedTexture = null;
      this.preloadedUrl = null;
      this.applyLoadedTexture(texture, intensity, blur, rotation);
      return;
    }

    // Cache miss — clear stale preload and load fresh
    if (this.preloadedTexture) {
      this.preloadedTexture.dispose();
      this.preloadedTexture = null;
      this.preloadedUrl = null;
    }

    const isExr = url.toLowerCase().endsWith('.exr');

    loadWithCdnFallback(url, (resolvedUrl) => this.fetchSkyboxTexture(resolvedUrl, isExr), { assetType: 'skybox' })
      .then((texture) => {
        if (this.disposed) {
          texture.dispose();
          return;
        }
        texture.colorSpace = isExr ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace;
        texture.mapping = THREE.EquirectangularReflectionMapping;
        const data = (texture.source?.data as { byteLength?: number } | null | undefined);
        logBigBuffer('skybox loaded', data?.byteLength ?? null, {
          url,
          isExr,
          kind: 'load',
        });
        this.applyLoadedTexture(texture, intensity, blur, rotation);
      })
      .catch((error: unknown) => {
        console.error('Failed to load skybox texture:', error);
      });
  }

  /** @deprecated Use loadSkyboxTexture instead */
  setBackgroundTexture(url: string): void {
    this.loadSkyboxTexture(url, 1.0, 0.0, [0, 0, 0]);
  }

  /**
   * Free GPU memory held by skybox textures and clear scene references.
   * Critical for iOS — HDR EXR skybox can hold 30–128 MB per scene; without
   * this, memory leaks across scene teardowns and leads to OOM.
   *
   * Any in-flight preload/load callbacks will see the incremented
   * `preloadAbortId` and dispose their texture on arrival instead of assigning
   * it to the instance (matches existing abort pattern in preloadSkyboxTexture).
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.preloadAbortId++;
    this.fading = false;
    // Critical for iOS: texture.dispose() releases the WebGL side, but the
    // CPU-side parsed data (Float32Array for HDR EXR, ImageBitmap for PNG/JPG)
    // in texture.source.data stays alive until JS GC. 4K EXR source = 128 MB.
    // Explicitly null it so JSC can reclaim immediately.
    if (this.loadedTexture) {
      const data = (this.loadedTexture.source?.data as { byteLength?: number } | null | undefined);
      logBigBuffer('skybox disposed (loaded)', data?.byteLength ?? null, {
        url: this.skyboxUrl,
        kind: 'dispose-loaded',
      });
      if (this.loadedTexture.source) {
        this.loadedTexture.source.data = null;
      }
      this.loadedTexture.dispose();
      this.loadedTexture = null;
    }
    if (this.preloadedTexture) {
      const data = (this.preloadedTexture.source?.data as { byteLength?: number } | null | undefined);
      logBigBuffer('skybox disposed (preloaded)', data?.byteLength ?? null, {
        url: this.preloadedUrl,
        kind: 'dispose-preloaded',
      });
      if (this.preloadedTexture.source) {
        this.preloadedTexture.source.data = null;
      }
      this.preloadedTexture.dispose();
      this.preloadedTexture = null;
      this.preloadedUrl = null;
    }
    this.scene.background = null;
    this.scene.environment = null;
  }
}
